import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const agriculturalModule: ChecklistModuleDefinition = {
  code: 'agricultural',
  name: 'Agricultural',
  description: 'Farming history, productivity, and infrastructure',
  categories: [
    {
      code: 'farm_history',
      name: 'Farm History',
      items: [
        {
          code: 'farming_history',
          title: 'Farming history',
          description: 'Historical agricultural use',
          isGroupTask: false,
          dataFields: [
            { key: 'current_use', label: 'Current Use', type: 'string' },
            { key: 'historical_uses', label: 'Historical Uses', type: 'array' },
            { key: 'years_in_production', label: 'Years in Production', type: 'number' },
            { key: 'fallow_years', label: 'Years Fallow', type: 'number' },
          ],
        },
        {
          code: 'crop_history',
          title: 'Crop history',
          description: 'What has been grown',
          isGroupTask: false,
          dataFields: [
            { key: 'crops_grown', label: 'Crops Grown', type: 'array' },
            { key: 'rotation_pattern', label: 'Rotation Pattern', type: 'string' },
            { key: 'yield_history', label: 'Yield History', type: 'string' },
          ],
        },
        {
          code: 'chemical_history',
          title: 'Chemical/pesticide history',
          description: 'Chemical application history',
          isGroupTask: false,
          dataFields: [
            { key: 'pesticides_used', label: 'Pesticides Used', type: 'boolean' },
            { key: 'herbicides_used', label: 'Herbicides Used', type: 'boolean' },
            { key: 'chemical_records_available', label: 'Records Available', type: 'boolean' },
            { key: 'organic_eligible', label: 'Organic Eligible', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Organic certified or eligible' },
            { flag: 'yellow', condition: 'Heavy chemical use history' },
          ],
        },
      ],
    },
    {
      code: 'productivity',
      name: 'Productivity',
      items: [
        {
          code: 'tillable_acres',
          title: 'Tillable acreage',
          description: 'Land suitable for row crops',
          isGroupTask: false,
          dataFields: [
            { key: 'total_tillable', label: 'Total Tillable Acres', type: 'number', unit: 'acres' },
            { key: 'percent_tillable', label: 'Percent Tillable', type: 'number', unit: 'percent' },
            { key: 'currently_in_production', label: 'Currently in Production', type: 'number', unit: 'acres' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'High percentage tillable' },
          ],
        },
        {
          code: 'pasture_acres',
          title: 'Pasture/grazing acreage',
          description: 'Land suitable for livestock',
          isGroupTask: false,
          dataFields: [
            { key: 'improved_pasture', label: 'Improved Pasture', type: 'number', unit: 'acres' },
            { key: 'native_pasture', label: 'Native Pasture', type: 'number', unit: 'acres' },
            { key: 'total_pasture', label: 'Total Pasture', type: 'number', unit: 'acres' },
          ],
        },
        {
          code: 'carrying_capacity',
          title: 'Livestock carrying capacity',
          description: 'How many animals land can support',
          isGroupTask: false,
          dataFields: [
            { key: 'animal_units', label: 'Animal Units', type: 'number' },
            { key: 'acres_per_au', label: 'Acres per AU', type: 'number' },
            { key: 'seasonal_limitations', label: 'Seasonal Limitations', type: 'string' },
          ],
        },
        {
          code: 'productivity_index',
          title: 'Productivity index',
          description: 'Soil productivity ratings',
          isGroupTask: false,
          dataFields: [
            { key: 'csr_rating', label: 'CSR Rating', type: 'number' },
            { key: 'productivity_index', label: 'Productivity Index', type: 'number' },
            { key: 'comparison_to_county', label: 'Comparison to County Avg', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Above average productivity' },
            { flag: 'yellow', condition: 'Below average productivity' },
          ],
        },
        {
          code: 'yield_data',
          title: 'Historical yield data',
          description: 'Documented production yields',
          isGroupTask: false,
          dataFields: [
            { key: 'yields_available', label: 'Yield Data Available', type: 'boolean' },
            { key: 'average_yields', label: 'Average Yields', type: 'string' },
            { key: 'compared_to_county', label: 'Compared to County', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'infrastructure',
      name: 'Agricultural Infrastructure',
      items: [
        {
          code: 'irrigation_system',
          title: 'Irrigation system',
          description: 'Irrigation infrastructure',
          isGroupTask: false,
          dataFields: [
            { key: 'irrigation_type', label: 'Irrigation Type', type: 'select', options: [
              { value: 'none', label: 'None' },
              { value: 'pivot', label: 'Center Pivot' },
              { value: 'drip', label: 'Drip/Micro' },
              { value: 'flood', label: 'Flood/Furrow' },
              { value: 'sprinkler', label: 'Sprinkler' },
              { value: 'traveling_gun', label: 'Traveling Gun' },
            ]},
            { key: 'irrigated_acres', label: 'Irrigated Acres', type: 'number', unit: 'acres' },
            { key: 'system_condition', label: 'System Condition', type: 'string' },
            { key: 'water_source', label: 'Water Source', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Good irrigation system in place' },
          ],
        },
        {
          code: 'drainage_system',
          title: 'Drainage systems',
          description: 'Tile drainage and water management',
          isGroupTask: false,
          dataFields: [
            { key: 'tile_drainage', label: 'Tile Drainage', type: 'boolean' },
            { key: 'drained_acres', label: 'Drained Acres', type: 'number', unit: 'acres' },
            { key: 'drainage_condition', label: 'Drainage Condition', type: 'string' },
            { key: 'surface_drainage', label: 'Surface Drainage', type: 'string' },
          ],
        },
        {
          code: 'grain_storage',
          title: 'Grain storage',
          description: 'On-farm grain storage',
          isGroupTask: false,
          dataFields: [
            { key: 'storage_type', label: 'Storage Type', type: 'array' },
            { key: 'total_capacity', label: 'Total Capacity', type: 'number', unit: 'bushels' },
            { key: 'storage_condition', label: 'Condition', type: 'string' },
            { key: 'drying_capacity', label: 'Drying Capacity', type: 'string' },
          ],
        },
        {
          code: 'livestock_facilities',
          title: 'Livestock facilities',
          description: 'Animal handling infrastructure',
          isGroupTask: false,
          dataFields: [
            { key: 'facility_types', label: 'Facility Types', type: 'multiselect', options: [
              { value: 'barn', label: 'Barn' },
              { value: 'loading_chute', label: 'Loading Chute' },
              { value: 'squeeze_chute', label: 'Squeeze Chute' },
              { value: 'corrals', label: 'Corrals' },
              { value: 'feeding_area', label: 'Feeding Area' },
              { value: 'shelter', label: 'Shelter/Loafing Shed' },
              { value: 'milking_parlor', label: 'Milking Parlor' },
            ]},
            { key: 'capacity', label: 'Capacity', type: 'string' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
        {
          code: 'equipment_storage',
          title: 'Equipment storage',
          description: 'Equipment storage buildings',
          isGroupTask: false,
          dataFields: [
            { key: 'storage_buildings', label: 'Storage Buildings', type: 'number' },
            { key: 'total_sqft', label: 'Total Square Feet', type: 'number', unit: 'sq ft' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'farm_programs',
      name: 'Farm Programs',
      items: [
        {
          code: 'crp_status',
          title: 'CRP status',
          description: 'Conservation Reserve Program',
          isGroupTask: false,
          dataFields: [
            { key: 'crp_acres', label: 'CRP Acres', type: 'number', unit: 'acres' },
            { key: 'contract_expiration', label: 'Contract Expiration', type: 'date' },
            { key: 'annual_payment', label: 'Annual Payment', type: 'number', unit: 'dollars' },
            { key: 'early_termination_penalty', label: 'Early Termination Penalty', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'CRP income in place' },
            { flag: 'yellow', condition: 'CRP restrictions on land use' },
          ],
        },
        {
          code: 'usda_programs',
          title: 'USDA program enrollment',
          description: 'Other USDA programs',
          isGroupTask: false,
          dataFields: [
            { key: 'programs_enrolled', label: 'Programs Enrolled', type: 'array' },
            { key: 'base_acres', label: 'Base Acres', type: 'number', unit: 'acres' },
            { key: 'payment_history', label: 'Payment History', type: 'string' },
          ],
        },
        {
          code: 'organic_certification',
          title: 'Organic certification',
          description: 'Organic status and eligibility',
          isGroupTask: false,
          dataFields: [
            { key: 'certified_organic', label: 'Certified Organic', type: 'boolean' },
            { key: 'transition_status', label: 'Transition Status', type: 'string' },
            { key: 'certifying_agency', label: 'Certifying Agency', type: 'string' },
            { key: 'certified_acres', label: 'Certified Acres', type: 'number', unit: 'acres' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Organic certified' },
            { flag: 'green', condition: 'Organic transitional' },
          ],
        },
      ],
    },
    {
      code: 'current_leases',
      name: 'Current Leases',
      items: [
        {
          code: 'farm_lease',
          title: 'Current farm lease',
          description: 'Existing farm lease terms',
          isGroupTask: false,
          dataFields: [
            { key: 'lease_exists', label: 'Lease Exists', type: 'boolean' },
            { key: 'tenant_name', label: 'Tenant Name', type: 'string' },
            { key: 'lease_type', label: 'Lease Type', type: 'select', options: [
              { value: 'cash', label: 'Cash Rent' },
              { value: 'crop_share', label: 'Crop Share' },
              { value: 'flex', label: 'Flex Lease' },
              { value: 'custom', label: 'Custom' },
            ]},
            { key: 'annual_rent', label: 'Annual Rent', type: 'number', unit: 'dollars' },
            { key: 'lease_expiration', label: 'Lease Expiration', type: 'date' },
            { key: 'termination_notice', label: 'Termination Notice Required', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Good lease income in place' },
            { flag: 'yellow', condition: 'Lease conflicts with plans' },
          ],
        },
        {
          code: 'grazing_lease',
          title: 'Grazing lease',
          description: 'Existing grazing/pasture lease',
          isGroupTask: false,
          dataFields: [
            { key: 'lease_exists', label: 'Lease Exists', type: 'boolean' },
            { key: 'lease_rate', label: 'Lease Rate', type: 'number', unit: 'dollars per acre' },
            { key: 'lease_term', label: 'Lease Term', type: 'string' },
            { key: 'annual_income', label: 'Annual Income', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
  ],
};

